import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ReactApexChart from "react-apexcharts";
import EmptyState from "../components/common/EmptyState";
import RetryBlock from "../components/common/RetryBlock";
import useApiRequest from "../hooks/useApiRequest";
import useShellContext from "../layout/useShellContext";
import {
  getAnalyticsFilterOptions,
  getAnalyticsInsights,
} from "../services/analyticsApi";
import { DATE_RANGE_OPTIONS, resolveDateRangeFilter } from "../utils/dateRanges";
import { formatNow } from "../utils/formatters";

const EMPTY_ANALYTICS_DATA = {
  insights: {},
  filterOptions: {
    agents: [],
    campaigns: [],
    outcomes: ["booked", "requested", "info", "dropped"],
    directions: ["inbound", "outbound"],
  },
};

const dayLabels = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function numberFormatter(value) {
  return new Intl.NumberFormat("en-US").format(Number(value || 0));
}

function formatDuration(totalSeconds) {
  const seconds = Number(totalSeconds || 0);
  if (!Number.isFinite(seconds) || seconds <= 0) {
    return "--";
  }

  const minutes = Math.floor(seconds / 60);
  const remainder = Math.floor(seconds % 60);
  return `${minutes}:${String(remainder).padStart(2, "0")}`;
}

function formatPercent(value) {
  const parsed = Number(value || 0);
  return `${parsed.toFixed(parsed % 1 === 0 ? 0 : 1)}%`;
}

function formatCurrencyAed(value) {
  const parsed = Number(value || 0);
  if (!Number.isFinite(parsed) || parsed <= 0) {
    return "--";
  }

  return `AED ${new Intl.NumberFormat("en-US", {
    minimumFractionDigits: parsed % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(parsed)}`;
}

function formatShortDate(value) {
  if (!value) {
    return "--";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "--";
  }

  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "2-digit",
  }).format(date);
}

function titleCase(value) {
  return String(value || "")
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function hasSeriesValues(values = []) {
  return values.some((value) => Number(value || 0) > 0);
}

function buildLineChartOptions(labels, colors) {
  return {
    chart: {
      toolbar: { show: false },
      zoom: { enabled: false },
      fontFamily: "Inter, system-ui, sans-serif",
    },
    colors,
    dataLabels: { enabled: false },
    grid: { borderColor: "#e6edf5", strokeDashArray: 4 },
    legend: { position: "top", horizontalAlign: "left" },
    stroke: { curve: "smooth", width: 3 },
    xaxis: {
      categories: labels,
      labels: { style: { colors: "#74839a" } },
    },
    yaxis: {
      min: 0,
      forceNiceScale: true,
      labels: { style: { colors: "#74839a" } },
    },
    tooltip: { shared: true, intersect: false },
  };
}

function PlanUsageCard({ plan }) {
  const consumedPercent = Number(plan?.consumed_percent || 0);
  const configured = Boolean(plan?.configured);
  const boundedPercent = Math.min(100, Math.max(0, consumedPercent));
  const allowanceMinutes = Number(plan?.allowance_minutes || 0);
  const remainingMinutes = plan?.remaining_minutes === null || plan?.remaining_minutes === undefined
    ? null
    : Number(plan.remaining_minutes || 0);
  const agentLimit = Number(plan?.agent_limit || 0);
  const activeUsers = Number(plan?.active_user_count || 0);
  const overageCount = Number(plan?.agent_overage_count || 0);
  const cycleStart = formatShortDate(plan?.cycle_start);
  const cycleEnd = formatShortDate(plan?.cycle_end);
  const languageLimit = plan?.language_limit ? titleCase(plan.language_limit) : "--";
  const projectedMinutes = Number(plan?.projected_minutes || 0);
  const daysLeft = Number(plan?.days_left || 0);

  return (
    <section className="analytics-card analytics-card-wide">
      <div className="analytics-card-header">
        <div>
          <p className="analytics-eyebrow">Plan Usage</p>
          <h3>{plan?.plan_name || "Unconfigured plan"}</h3>
        </div>
        <span className={`analytics-status-pill ${configured ? "is-active" : ""}`}>
          {configured ? plan?.billing_status || "active" : "setup needed"}
        </span>
      </div>
      <div className="analytics-plan-main">
        <div>
          <strong>{numberFormatter(plan?.used_minutes)} min</strong>
          <span>used this cycle</span>
        </div>
        <div>
          <strong>{configured ? numberFormatter(plan?.remaining_minutes) : "--"} min</strong>
          <span>remaining</span>
        </div>
        <div>
          <strong>{formatPercent(consumedPercent)}</strong>
          <span>consumed</span>
        </div>
      </div>
      <div className="analytics-plan-visual">
        <div className="analytics-plan-ring-card">
          <div
            className="analytics-plan-ring"
            style={{ "--plan-progress": `${boundedPercent}%` }}
            aria-label={`${formatPercent(consumedPercent)} of plan minutes consumed`}
          >
            <div className="analytics-plan-ring-inner">
              <strong>{formatPercent(consumedPercent)}</strong>
              <span>used</span>
            </div>
          </div>
          <div className="analytics-plan-ring-copy">
            <strong>{configured ? `${numberFormatter(allowanceMinutes)} min` : "No allowance"}</strong>
            <span>Plan capacity</span>
          </div>
        </div>

        <div className="analytics-plan-infographic">
          <div className="analytics-plan-timeline">
            <div className="analytics-plan-timeline-top">
              <span>Billing cycle</span>
              <strong>{configured ? `${cycleStart} - ${cycleEnd}` : "Setup needed"}</strong>
            </div>
            <div className="analytics-progress-track" aria-label="Plan minutes used">
              <div
                className="analytics-progress-fill"
                style={{ width: `${boundedPercent}%` }}
              />
            </div>
            <p>
              {configured
                ? `${numberFormatter(daysLeft)} days left, projected ${numberFormatter(projectedMinutes)} min by cycle end`
                : "Configure plan minutes in Settings > Billing to enable usage tracking."}
            </p>
          </div>

          <div className="analytics-plan-detail-grid">
            <div className="analytics-plan-detail">
              <span>Agents</span>
              <strong>{agentLimit ? `${numberFormatter(activeUsers)} / ${numberFormatter(agentLimit)}` : "--"}</strong>
              <small>{overageCount > 0 ? `${numberFormatter(overageCount)} over limit` : "Current users"}</small>
            </div>
            <div className="analytics-plan-detail">
              <span>Languages</span>
              <strong>{languageLimit}</strong>
              <small>Included</small>
            </div>
            <div className="analytics-plan-detail">
              <span>Monthly</span>
              <strong>{formatCurrencyAed(plan?.monthly_cost_aed)}</strong>
              <small>{plan?.discount_percent ? `${numberFormatter(plan.discount_percent)}% off` : "Plan cost"}</small>
            </div>
            <div className="analytics-plan-detail">
              <span>Setup</span>
              <strong>{formatCurrencyAed(plan?.setup_fee_aed)}</strong>
              <small>One time</small>
            </div>
            <div className="analytics-plan-detail">
              <span>Extra mins</span>
              <strong>{formatCurrencyAed(plan?.additional_minute_rate_aed)}</strong>
              <small>Per minute</small>
            </div>
            <div className="analytics-plan-detail">
              <span>Remaining</span>
              <strong>{configured && remainingMinutes !== null ? `${numberFormatter(remainingMinutes)} min` : "--"}</strong>
              <small>Available</small>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function SentimentCard({ sentiment }) {
  const totals = sentiment?.totals || {};
  const values = [
    Number(totals.positive || 0),
    Number(totals.neutral || 0),
    Number(totals.negative || 0),
    Number(totals.unknown || 0),
  ];
  const trend = sentiment?.trend || [];
  const labels = trend.map((item) => String(item.call_date || "").slice(5));
  const trendSeries = [
    { name: "Positive", data: trend.map((item) => Number(item.positive || 0)) },
    { name: "Neutral", data: trend.map((item) => Number(item.neutral || 0)) },
    { name: "Negative", data: trend.map((item) => Number(item.negative || 0)) },
  ];

  return (
    <section className="analytics-card">
      <div className="analytics-card-header">
        <div>
          <p className="analytics-eyebrow">Sentiment</p>
          <h3>Positive / Neutral / Negative</h3>
        </div>
      </div>
      {hasSeriesValues(values) ? (
        <div className="analytics-split-chart">
          <ReactApexChart
            type="donut"
            height={230}
            series={values}
            options={{
              labels: ["Positive", "Neutral", "Negative", "Unknown"],
              colors: ["#21c58b", "#5ba0e6", "#ff6b6b", "#9aa4b2"],
              legend: { position: "bottom" },
              dataLabels: { enabled: false },
              plotOptions: {
                pie: { donut: { size: "68%" } },
              },
            }}
          />
          <ReactApexChart
            type="line"
            height={160}
            series={trendSeries}
            options={buildLineChartOptions(labels, ["#21c58b", "#5ba0e6", "#ff6b6b"])}
          />
        </div>
      ) : (
        <EmptyState title="No sentiment yet" subtitle="Sentiment appears after call summaries include labels." />
      )}
    </section>
  );
}

function CallsPerDayCard({ rows }) {
  const labels = rows.map((item) => String(item.call_date || "").slice(5));
  const series = [
    { name: "Inbound", type: "column", data: rows.map((item) => Number(item.inbound_calls || 0)) },
    { name: "Outbound", type: "column", data: rows.map((item) => Number(item.outbound_calls || 0)) },
    { name: "7-day avg", type: "line", data: rows.map((item) => Number(item.moving_average_7d || 0)) },
  ];

  return (
    <section className="analytics-card analytics-card-wide">
      <div className="analytics-card-header">
        <div>
          <p className="analytics-eyebrow">Trend</p>
          <h3>Calls Per Day</h3>
        </div>
      </div>
      {rows.length ? (
        <ReactApexChart
          type="line"
          height={330}
          series={series}
          options={{
            ...buildLineChartOptions(labels, ["#5ba0e6", "#f4b860", "#0fa9c0"]),
            chart: {
              ...buildLineChartOptions(labels, []).chart,
              stacked: false,
            },
            plotOptions: {
              bar: { columnWidth: "45%", borderRadius: 5 },
            },
            stroke: { width: [0, 0, 3], curve: "smooth" },
          }}
        />
      ) : (
        <EmptyState title="No call trend data" subtitle="Daily inbound and outbound trends will appear here." />
      )}
    </section>
  );
}

function DurationDistributionCard({ rows }) {
  const labels = rows.map((item) => item.label);
  const values = rows.map((item) => Number(item.count || 0));

  return (
    <section className="analytics-card">
      <div className="analytics-card-header">
        <div>
          <p className="analytics-eyebrow">Duration</p>
          <h3>Distribution</h3>
        </div>
      </div>
      {hasSeriesValues(values) ? (
        <ReactApexChart
          type="bar"
          height={280}
          series={[{ name: "Calls", data: values }]}
          options={{
            chart: { toolbar: { show: false }, fontFamily: "Inter, system-ui, sans-serif" },
            colors: ["#0fa9c0"],
            dataLabels: { enabled: false },
            grid: { borderColor: "#e6edf5", strokeDashArray: 4 },
            plotOptions: { bar: { borderRadius: 8, columnWidth: "48%" } },
            xaxis: { categories: labels },
            yaxis: { min: 0, forceNiceScale: true },
          }}
        />
      ) : (
        <EmptyState title="No connected duration" subtitle="Duration buckets will populate once calls have talk time." />
      )}
    </section>
  );
}

function OutcomesCard({ outcomes }) {
  const labels = outcomes.map((item) => titleCase(item.outcome));
  const values = outcomes.map((item) => Number(item.count || 0));

  return (
    <section className="analytics-card">
      <div className="analytics-card-header">
        <div>
          <p className="analytics-eyebrow">Outcomes</p>
          <h3>Conversion Mix</h3>
        </div>
      </div>
      {hasSeriesValues(values) ? (
        <>
          <ReactApexChart
            type="donut"
            height={250}
            series={values}
            options={{
              labels,
              colors: ["#21c58b", "#5ba0e6", "#f4b860", "#9aa4b2"],
              dataLabels: { enabled: false },
              legend: { position: "bottom" },
              plotOptions: { pie: { donut: { size: "68%" } } },
            }}
          />
          <div className="analytics-outcome-rates">
            {outcomes.map((item) => (
              <span key={item.outcome}>{titleCase(item.outcome)} {formatPercent(item.rate)}</span>
            ))}
          </div>
        </>
      ) : (
        <EmptyState title="No outcomes" subtitle="Booked, requested, info, and dropped ratios will appear here." />
      )}
    </section>
  );
}

function HeatmapCard({ rows }) {
  const map = new Map();
  rows.forEach((row) => {
    map.set(`${Number(row.day_of_week || 0)}-${Number(row.hour_of_day || 0)}`, Number(row.total || 0));
  });
  const series = dayLabels.map((label, index) => ({
    name: label,
    data: Array.from({ length: 24 }, (_, hour) => ({
      x: `${hour}:00`,
      y: map.get(`${index + 1}-${hour}`) || 0,
    })),
  }));
  const hasData = rows.some((row) => Number(row.total || 0) > 0);
  const formatHourLabel = (value, interval = 3) => {
    const hour = Number.parseInt(String(value || "").split(":")[0], 10);
    return Number.isFinite(hour) && hour % interval === 0 ? `${hour}:00` : "";
  };
  const tooltipCustom = ({ series: tooltipSeries, seriesIndex, dataPointIndex, w }) => {
    const day = w?.config?.series?.[seriesIndex]?.name || "Day";
    const point = w?.config?.series?.[seriesIndex]?.data?.[dataPointIndex] || {};
    const hour = point.x || `${dataPointIndex}:00`;
    const count = tooltipSeries?.[seriesIndex]?.[dataPointIndex] ?? point.y ?? 0;
    const label = Number(count) === 1 ? "call" : "calls";

    return `
      <div class="analytics-heatmap-tooltip">
        <strong>${day} ${hour}</strong>
        <span>${count} ${label}</span>
      </div>
    `;
  };

  return (
    <section className="analytics-card analytics-card-wide">
      <div className="analytics-card-header">
        <div>
          <p className="analytics-eyebrow">Timing</p>
          <h3>Calls By Hour And Day</h3>
        </div>
      </div>
      {hasData ? (
        <ReactApexChart
          type="heatmap"
          height={330}
          series={series}
          options={{
            chart: { toolbar: { show: false }, fontFamily: "Inter, system-ui, sans-serif" },
            dataLabels: { enabled: false },
            colors: ["#0fa9c0"],
            xaxis: {
              type: "category",
              tickPlacement: "on",
              axisTicks: { show: false },
              labels: {
                rotate: 0,
                hideOverlappingLabels: true,
                trim: false,
                formatter: (value) => formatHourLabel(value, 3),
              },
            },
            tooltip: {
              enabled: true,
              custom: tooltipCustom,
            },
            plotOptions: {
              heatmap: {
                shadeIntensity: 0.45,
                radius: 4,
                colorScale: {
                  ranges: [
                    { from: 0, to: 0, color: "#eef4f9", name: "None" },
                    { from: 1, to: 3, color: "#b9e8f0", name: "Low" },
                    { from: 4, to: 8, color: "#5fc6d5", name: "Medium" },
                    { from: 9, to: 999, color: "#0fa9c0", name: "High" },
                  ],
                },
              },
            },
            responsive: [
              {
                breakpoint: 700,
                options: {
                  xaxis: {
                    labels: {
                      rotate: 0,
                      hideOverlappingLabels: true,
                      formatter: (value) => formatHourLabel(value, 4),
                    },
                  },
                },
              },
            ],
          }}
        />
      ) : (
        <EmptyState title="No time distribution" subtitle="Hourly activity will appear when calls are available." />
      )}
    </section>
  );
}

function AgentPerformanceCard({ rows }) {
  return (
    <section className="analytics-card analytics-card-wide">
      <div className="analytics-card-header">
        <div>
          <p className="analytics-eyebrow">Agents</p>
          <h3>Per-Agent Performance</h3>
        </div>
      </div>
      {rows.length ? (
        <div className="analytics-table-wrap">
          <table className="analytics-table">
            <thead>
              <tr>
                <th>Agent</th>
                <th>Volume</th>
                <th>Connected</th>
                <th>Booking Rate</th>
                <th>Avg Duration</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={`${row.agent_id}-${row.agent_name}`}>
                  <td>{row.agent_name}</td>
                  <td>{numberFormatter(row.total_calls)}</td>
                  <td>{numberFormatter(row.connected_calls)}</td>
                  <td>{formatPercent(row.booking_rate)}</td>
                  <td>{formatDuration(row.avg_duration_seconds)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <EmptyState title="No agent data" subtitle="Agent performance appears when calls are assigned." />
      )}
    </section>
  );
}

function ConnectionTrendCard({ rows }) {
  const labels = rows.map((item) => String(item.call_date || "").slice(5));
  const series = [
    { name: "Connected", data: rows.map((item) => Number(item.connected || 0)) },
    { name: "Failed", data: rows.map((item) => Number(item.failed || 0)) },
  ];

  return (
    <section className="analytics-card">
      <div className="analytics-card-header">
        <div>
          <p className="analytics-eyebrow">Connection</p>
          <h3>Connected vs Failed</h3>
        </div>
      </div>
      {rows.length ? (
        <ReactApexChart
          type="line"
          height={280}
          series={series}
          options={buildLineChartOptions(labels, ["#21c58b", "#ff6b6b"])}
        />
      ) : (
        <EmptyState title="No connection trend" subtitle="Connected and failed trends will appear here." />
      )}
    </section>
  );
}

function LeadTaskCard({ rows }) {
  const usefulRows = rows.filter((row) => Number(row.total || 0) > 0);
  if (usefulRows.length <= 1) {
    return null;
  }

  return (
    <section className="analytics-card">
      <div className="analytics-card-header">
        <div>
          <p className="analytics-eyebrow">Lead Tasks</p>
          <h3>Task Distribution</h3>
        </div>
      </div>
      <ReactApexChart
        type="donut"
        height={280}
        series={usefulRows.map((row) => Number(row.total || 0))}
        options={{
          labels: usefulRows.map((row) => titleCase(row.label)),
          colors: ["#0fa9c0", "#5ba0e6", "#f4b860", "#21c58b", "#9aa4b2"],
          dataLabels: { enabled: false },
          legend: { position: "bottom" },
        }}
      />
    </section>
  );
}

export default function AnalyticsPage() {
  const { registerPageControls, resetPageControls } = useShellContext();
  const { data, error, run } = useApiRequest(EMPTY_ANALYTICS_DATA);
  const [lastUpdated, setLastUpdated] = useState("--");
  const [shellRefreshing, setShellRefreshing] = useState(false);
  const [dateRange, setDateRange] = useState("30d");
  const [customDateFrom, setCustomDateFrom] = useState("");
  const [customDateTo, setCustomDateTo] = useState("");
  const [direction, setDirection] = useState("");
  const [agentId, setAgentId] = useState("");
  const [outcome, setOutcome] = useState("");
  const [campaignId, setCampaignId] = useState("");
  const mountedRef = useRef(false);

  const dateFilters = useMemo(
    () => resolveDateRangeFilter(dateRange, customDateFrom, customDateTo),
    [customDateFrom, customDateTo, dateRange],
  );

  const analyticsQuery = useMemo(
    () => ({
      date_from: dateFilters.date_from,
      date_to: dateFilters.date_to,
      direction,
      agent_id: agentId,
      outcome,
      campaign_id: campaignId,
    }),
    [agentId, campaignId, dateFilters.date_from, dateFilters.date_to, direction, outcome],
  );

  const loadAnalytics = useCallback(
    async ({ signal } = {}) => {
      const [insights, filterOptions] = await Promise.all([
        getAnalyticsInsights({ signal, query: analyticsQuery }),
        getAnalyticsFilterOptions({ signal }),
      ]);

      return { insights, filterOptions };
    },
    [analyticsQuery],
  );

  const loadPageData = useCallback(
    async ({ trackRefresh = false } = {}) => {
      if (trackRefresh && mountedRef.current) {
        setShellRefreshing(true);
      }

      try {
        const result = await run(loadAnalytics);
        if (mountedRef.current) {
          setLastUpdated(formatNow());
        }
        return result;
      } finally {
        if (trackRefresh && mountedRef.current) {
          setShellRefreshing(false);
        }
      }
    },
    [loadAnalytics, run],
  );

  const handleRefresh = useCallback(
    () => loadPageData({ trackRefresh: true }),
    [loadPageData],
  );

  const handleClear = () => {
    setDateRange("30d");
    setCustomDateFrom("");
    setCustomDateTo("");
    setDirection("");
    setAgentId("");
    setOutcome("");
    setCampaignId("");
  };

  useEffect(() => {
    mountedRef.current = true;
    loadPageData().catch(() => undefined);

    return () => {
      mountedRef.current = false;
    };
  }, [loadPageData]);

  useEffect(() => {
    registerPageControls({
      onRefresh: handleRefresh,
      refreshEvery: "Manual",
      lastUpdated,
      isRefreshing: shellRefreshing,
    });

    return () => resetPageControls();
  }, [handleRefresh, lastUpdated, registerPageControls, resetPageControls, shellRefreshing]);

  const insights = data?.insights || {};
  const filterOptions = data?.filterOptions || EMPTY_ANALYTICS_DATA.filterOptions;
  const campaigns = Array.isArray(filterOptions.campaigns) ? filterOptions.campaigns : [];
  const agents = Array.isArray(filterOptions.agents) ? filterOptions.agents : [];

  return (
    <div className="analytics-page">
      {error ? (
        <RetryBlock
          message="Analytics data is unavailable right now."
          onRetry={() => handleRefresh().catch(() => undefined)}
        />
      ) : null}

      <div className="section-row analytics-toolbar">
        <div>
          <div className="section-title section-title-no-margin">Analytics</div>
          <div className="page-subtitle">
            Trends, ratios, sentiment, and usage across your calling operation.
          </div>
        </div>
      </div>

      <div className="analytics-filter-bar">
        <select className="filter-select" value={dateRange} onChange={(event) => setDateRange(event.target.value)}>
          {DATE_RANGE_OPTIONS.filter((option) => option.value !== "").map((option) => (
            <option key={option.value} value={option.value}>{option.label}</option>
          ))}
        </select>
        {dateRange === "custom" ? (
          <>
            <input
              type="date"
              className="filter-date-input"
              value={customDateFrom}
              onChange={(event) => setCustomDateFrom(event.target.value)}
              aria-label="Analytics start date"
            />
            <input
              type="date"
              className="filter-date-input"
              value={customDateTo}
              onChange={(event) => setCustomDateTo(event.target.value)}
              aria-label="Analytics end date"
            />
          </>
        ) : null}
        <select className="filter-select" value={direction} onChange={(event) => setDirection(event.target.value)}>
          <option value="">All directions</option>
          <option value="inbound">Inbound</option>
          <option value="outbound">Outbound</option>
        </select>
        <select className="filter-select" value={agentId} onChange={(event) => setAgentId(event.target.value)}>
          <option value="">All agents</option>
          {agents.map((agent) => (
            <option key={agent.id} value={agent.id}>{agent.label}</option>
          ))}
        </select>
        <select className="filter-select" value={outcome} onChange={(event) => setOutcome(event.target.value)}>
          <option value="">All outcomes</option>
          {(filterOptions.outcomes || []).map((item) => (
            <option key={item} value={item}>{titleCase(item)}</option>
          ))}
        </select>
        {campaigns.length ? (
          <select className="filter-select" value={campaignId} onChange={(event) => setCampaignId(event.target.value)}>
            <option value="">All campaigns</option>
            {campaigns.map((campaign) => (
              <option key={campaign.id} value={campaign.id}>{campaign.label}</option>
            ))}
          </select>
        ) : null}
        <button type="button" className="analytics-clear-button" onClick={handleClear}>
          Clear
        </button>
      </div>

      <div className="analytics-grid analytics-grid-top">
        <PlanUsageCard plan={insights.plan_usage || {}} />
        <SentimentCard sentiment={insights.sentiment || {}} />
      </div>

      <div className="analytics-grid">
        <CallsPerDayCard rows={insights.calls_per_day || []} />
        <DurationDistributionCard rows={insights.duration_distribution || []} />
      </div>

      <div className="analytics-grid">
        <OutcomesCard outcomes={insights.outcomes || []} />
        <HeatmapCard rows={insights.time_distribution || []} />
      </div>

      <div className="analytics-grid">
        <AgentPerformanceCard rows={insights.agent_performance || []} />
        <ConnectionTrendCard rows={insights.connection_trend || []} />
        <LeadTaskCard rows={insights.lead_task_distribution || []} />
      </div>
    </div>
  );
}
